import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

function FaceVerification() {
  const [user, setUser] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState('unverified');
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [profilePhotos, setProfilePhotos] = useState([]);
  const [message, setMessage] = useState('');
  const [verificationHistory, setVerificationHistory] = useState([]);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    loadUserData();
    loadVerificationHistory();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);

      if (user) {
        // Get verification status from profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('verification_status, is_verified')
          .eq('id', user.id)
          .single();

        if (profile) {
          setVerificationStatus(profile.verification_status || 'unverified');
        }

        // Get profile photos for comparison
        const { data: photos } = await supabase
          .from('profile_photos')
          .select('photo_url')
          .eq('user_id', user.id)
          .order('upload_order', { ascending: true });

        setProfilePhotos(photos || []);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadVerificationHistory = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('face_verifications')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        setVerificationHistory(data || []);
      }
    } catch (error) {
      console.error('Error loading verification history:', error);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      });
      
      streamRef.current = stream;
      setShowCamera(true);
      
      // Wait for component to render, then set video source
      setTimeout(() => {
        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setMessage('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (video && canvas) {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      canvas.toBlob(async (blob) => {
        await uploadVerificationPhoto(blob);
      }, 'image/jpeg', 0.8);
    }
  };

  const uploadVerificationPhoto = async (blob) => {
    if (!user || profilePhotos.length === 0) {
      setMessage('You need at least one profile photo before verification.');
      return;
    }

    setUploading(true);
    setMessage('Processing verification...');
    console.log('Starting verification upload for user:', user.id);

    try {
      // Upload verification photo to storage
      const fileName = `verification/${user.id}/${Date.now()}.jpg`;
      console.log('Uploading file:', fileName);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, blob);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('Upload successful:', uploadData);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('profile-photos')
        .getPublicUrl(fileName);

      console.log('Public URL generated:', urlData.publicUrl);

      // Create verification record
      const verificationData = {
        user_id: user.id,
        verification_photo_url: urlData.publicUrl,
        profile_photo_url: profilePhotos[0].photo_url,
        status: 'pending'
      };

      console.log('Inserting verification record:', verificationData);

      const { data: dbData, error: dbError } = await supabase
        .from('face_verifications')
        .insert(verificationData);

      if (dbError) {
        console.error('Database error:', dbError);
        throw dbError;
      }

      console.log('Database insert successful:', dbData);

      // Update profile status to pending
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ verification_status: 'pending' })
        .eq('id', user.id);

      if (updateError) {
        console.error('Profile update error:', updateError);
        // Don't throw error for profile update - verification was still created
      }

      setVerificationStatus('pending');
      setMessage('Verification submitted! We\'ll review your photos within 24-48 hours.');
      stopCamera();
      loadVerificationHistory();

    } catch (error) {
      console.error('Error uploading verification:', error);
      setMessage(`Error submitting verification: ${error.message}. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage('Please select an image file.');
      return;
    }

    await uploadVerificationPhoto(file);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return '#28a745';
      case 'pending': return '#ffc107';
      case 'rejected': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return '✅';
      case 'pending': return '⏳';
      case 'rejected': return '❌';
      default: return '⚪';
    }
  };

  if (!user) {
    return <div>Please log in to access verification.</div>;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px 0'
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>
            Face Verification
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px',
            margin: 0
          }}>
            Get verified to show you're a real person ✨
          </p>
        </div>

        {/* Current Status */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '15px',
          padding: '25px',
          marginBottom: '30px',
          border: `2px solid ${getStatusColor(verificationStatus)}40`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '15px'
          }}>
            <span style={{ fontSize: '48px', marginRight: '15px' }}>
              {getStatusIcon(verificationStatus)}
            </span>
            <div>
              <h3 style={{
                color: getStatusColor(verificationStatus),
                fontSize: '24px',
                fontWeight: '600',
                margin: 0,
                textTransform: 'capitalize'
              }}>
                {verificationStatus}
              </h3>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px',
                margin: '5px 0 0 0'
              }}>
                {verificationStatus === 'verified' && 'Your profile is verified!'}
                {verificationStatus === 'pending' && 'Verification under review'}
                {verificationStatus === 'rejected' && 'Verification was rejected'}
                {verificationStatus === 'unverified' && 'Not yet verified'}
              </p>
            </div>
          </div>
        </div>

        {/* Verification Instructions */}
        {verificationStatus !== 'verified' && verificationStatus !== 'pending' && (
          <div style={{
            background: 'rgba(0, 233, 255, 0.1)',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '30px',
            border: '1px solid rgba(0, 233, 255, 0.3)'
          }}>
            <h3 style={{
              color: '#00E9FF',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              📋 How to get verified:
            </h3>
            <ol style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '14px',
              lineHeight: '1.6',
              paddingLeft: '20px'
            }}>
              <li>Make sure you have at least one profile photo uploaded</li>
              <li><strong>Write on white paper:</strong> "Flirteen" + today's date + current time</li>
              <li><strong>Hold the paper clearly visible</strong> next to your face in the selfie</li>
              <li>Take a clear selfie looking directly at the camera</li>
              <li>Ensure good lighting and your face is clearly visible</li>
              <li>No filters, sunglasses, or face coverings</li>
              <li>Both your face and the handwritten paper must be readable</li>
              <li>We'll compare your selfie with your profile photos</li>
            </ol>
            
            <div style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '10px',
              padding: '15px',
              marginTop: '15px'
            }}>
              <p style={{
                margin: '0',
                color: '#ffc107',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                📝 Example: Write "Flirteen {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}" on white paper
              </p>
            </div>
          </div>
        )}

        {/* Camera/Upload Section */}
        {verificationStatus !== 'verified' && verificationStatus !== 'pending' && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '15px',
            padding: '25px',
            marginBottom: '30px'
          }}>
            {!showCamera ? (
              <div style={{ textAlign: 'center' }}>
                <h3 style={{
                  color: '#00E9FF',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '10px'
                }}>
                  Take Verification Photo
                </h3>
                <div style={{
                  background: 'rgba(255, 193, 7, 0.15)',
                  border: '1px solid rgba(255, 193, 7, 0.4)',
                  borderRadius: '10px',
                  padding: '12px',
                  marginBottom: '20px'
                }}>
                  <p style={{
                    margin: 0,
                    color: '#ffc107',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    ✋ Don't forget: Hold white paper with "Flirteen + today's date + time" next to your face!
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={startCamera}
                    style={{
                      padding: '15px 25px',
                      background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif'
                    }}
                  >
                    📸 Use Camera
                  </button>
                  
                  <label style={{
                    padding: '15px 25px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'Poppins, sans-serif',
                    display: 'inline-block'
                  }}>
                    📁 Upload Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <h3 style={{
                  color: '#00E9FF',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '10px'
                }}>
                  Position your face in the center
                </h3>
                <div style={{
                  background: 'rgba(255, 193, 7, 0.15)',
                  border: '1px solid rgba(255, 193, 7, 0.4)',
                  borderRadius: '10px',
                  padding: '10px',
                  marginBottom: '15px',
                  maxWidth: '400px',
                  margin: '0 auto 15px auto'
                }}>
                  <p style={{
                    margin: 0,
                    color: '#ffc107',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    📄 Hold paper with "Flirteen {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}" clearly visible
                  </p>
                </div>
                
                <div style={{
                  position: 'relative',
                  display: 'inline-block',
                  borderRadius: '15px',
                  overflow: 'hidden',
                  marginBottom: '20px',
                  border: '2px solid #00E9FF'
                }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '400px',
                      height: '300px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  
                  {/* Face outline guide */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '200px',
                    height: '250px',
                    border: '3px solid #00E9FF',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                  }} />
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={capturePhoto}
                    disabled={uploading}
                    style={{
                      padding: '15px 25px',
                      background: uploading 
                        ? 'rgba(255, 255, 255, 0.3)' 
                        : 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      fontFamily: 'Poppins, sans-serif'
                    }}
                  >
                    {uploading ? '⏳ Processing...' : '📸 Capture'}
                  </button>
                  
                  <button
                    onClick={stopCamera}
                    style={{
                      padding: '15px 25px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontFamily: 'Poppins, sans-serif'
                    }}
                  >
                    ❌ Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Message Display */}
        {message && (
          <div style={{
            background: message.includes('Error') 
              ? 'rgba(220, 53, 69, 0.2)' 
              : message.includes('✅') 
              ? 'rgba(40, 167, 69, 0.2)'
              : 'rgba(0, 233, 255, 0.2)',
            border: `1px solid ${message.includes('Error') ? '#dc3545' : message.includes('✅') ? '#28a745' : '#00E9FF'}`,
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{
              margin: 0,
              color: message.includes('Error') ? '#ff6b6b' : message.includes('✅') ? '#51cf66' : '#00E9FF',
              fontWeight: '500'
            }}>
              {message}
            </p>
          </div>
        )}

        {/* Verification History */}
        {verificationHistory.length > 0 && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '15px',
            padding: '25px'
          }}>
            <h3 style={{
              color: '#00E9FF',
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '15px'
            }}>
              📊 Verification History
            </h3>
            
            {verificationHistory.map((verification) => (
              <div key={verification.id} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '10px',
                padding: '15px',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <span style={{
                    color: getStatusColor(verification.status),
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {getStatusIcon(verification.status)} {verification.status.toUpperCase()}
                  </span>
                  <p style={{
                    margin: '5px 0 0 0',
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}>
                    Submitted: {new Date(verification.created_at).toLocaleDateString()}
                  </p>
                  {verification.admin_notes && (
                    <p style={{
                      margin: '5px 0 0 0',
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontStyle: 'italic'
                    }}>
                      Note: {verification.admin_notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hidden canvas for photo capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
}

export default FaceVerification;